import React, { forwardRef, useCallback, useState, useEffect } from "react";
import { trackEvent, EventNames } from "./analytics";

/**
 * Higher-Order Component to add click tracking to any element
 */
export const withClickTracking = <P extends object>(
  Component: React.ComponentType<P>,
  trackingInfo: {
    category: string;
    action?: string;
    getLabel?: (props: P) => string;
    getValue?: (props: P) => number;
    nonInteraction?: boolean;
    additionalParams?:
      | Record<string, any>
      | ((props: P) => Record<string, any>);
  }
) => {
  const WithClickTracking = forwardRef<unknown, P>((props, ref) => {
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        const {
          category,
          action = "click",
          getLabel,
          getValue,
          nonInteraction,
          additionalParams,
        } = trackingInfo;

        const label = getLabel ? getLabel(props) : undefined;
        const value = getValue ? getValue(props) : undefined;
        const params =
          typeof additionalParams === "function"
            ? additionalParams(props)
            : additionalParams;

        trackEvent(action, {
          event_category: category,
          event_label: label,
          value,
          non_interaction: nonInteraction || false,
          ...params,
        });

        // Call the original onClick if it exists
        if (
          "onClick" in props &&
          typeof (props as any).onClick === "function"
        ) {
          (props as any).onClick(e);
        }
      },
      [props, trackingInfo]
    );

    return <Component {...props} onClick={handleClick} ref={ref} />;
  });

  WithClickTracking.displayName = `withClickTracking(${
    Component.displayName || Component.name || "Component"
  })`;
  return WithClickTracking;
};

/**
 * Hook to track element visibility in viewport
 */
export const useElementVisibility = (
  options: {
    threshold?: number | number[];
    onVisible?: () => void;
    onHidden?: () => void;
    trackEvent?: boolean;
    eventName?: string;
    eventParams?: Record<string, any>;
  } = {}
) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const {
    threshold = 0.1,
    onVisible,
    onHidden,
    trackEvent: shouldTrackEvent = false,
    eventName = "element_visible",
    eventParams = {},
  } = options;

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);

        if (visible) {
          onVisible?.();
          if (shouldTrackEvent) {
            trackEvent(eventName, {
              element_id: ref.id || undefined,
              element_class: ref.className || undefined,
              element_tag: ref.tagName.toLowerCase(),
              ...eventParams,
            });
          }
        } else {
          onHidden?.();
        }
      },
      { threshold }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [
    ref,
    threshold,
    onVisible,
    onHidden,
    shouldTrackEvent,
    eventName,
    eventParams,
  ]);

  return { ref: setRef, isVisible };
};

/**
 * Component to track page exit intent (when mouse moves toward top of page quickly)
 */
export const ExitIntentTracker: React.FC<{
  onExitIntent?: () => void;
  sensitivity?: number;
  timer?: number;
  eventName?: string;
}> = ({
  onExitIntent,
  sensitivity = 20,
  timer = 1000,
  eventName = "exit_intent",
}) => {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!isActive) return;

    let startTime: number;
    let exitTimer: ReturnType<typeof setTimeout> | undefined = undefined;

    const resetTimer = () => {
      if (exitTimer) clearTimeout(exitTimer);
      startTime = new Date().getTime();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (
        e.clientY <= sensitivity && // User's mouse is near the top of the page
        e.clientY > 0 && // Not already outside the viewport
        e.clientX > 0 &&
        e.clientX < window.innerWidth
      ) {
        const now = new Date().getTime();
        const timeSinceStart = now - startTime;

        if (timeSinceStart > timer) {
          trackEvent(eventName, {
            sensitivity,
            mouse_position_x: e.clientX,
            mouse_position_y: e.clientY,
            page_url: window.location.href,
          });

          onExitIntent?.();
          setIsActive(false);
        }
      }
    };

    // Initialize timer
    resetTimer();

    // Add event listeners
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (exitTimer) clearTimeout(exitTimer);
    };
  }, [sensitivity, timer, onExitIntent, isActive, eventName]);

  return null;
};

/**
 * Hook to track scroll depth with customizable thresholds
 */
export const useScrollDepthTracking = (
  thresholds: number[] = [25, 50, 75, 90, 100],
  options: {
    eventName?: string;
    nonInteraction?: boolean;
    throttleMs?: number;
  } = {}
) => {
  const {
    eventName = EventNames.SCROLL_DEPTH,
    nonInteraction = true,
    throttleMs = 250,
  } = options;

  useEffect(() => {
    // Skip all event listeners if no thresholds to track
    if (thresholds.length === 0) {
      return;
    }

    const trackedThresholds = new Set<number>();
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;

    const calculateScrollPercentage = (): number => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight
      );
      const trackLength = docHeight - windowHeight;
      return trackLength > 0
        ? Math.floor((scrollTop / trackLength) * 100)
        : 100;
    };

    const handleScroll = () => {
      // Skip tracking if timer is active
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        const scrollPercentage = calculateScrollPercentage();

        for (const threshold of thresholds) {
          if (
            scrollPercentage >= threshold &&
            !trackedThresholds.has(threshold)
          ) {
            trackedThresholds.add(threshold);

            trackEvent(eventName, {
              scroll_percentage: threshold,
              page_path: window.location.pathname,
              page_title: document.title,
              non_interaction: nonInteraction,
            });
          }
        }

        throttleTimer = null;
      }, throttleMs);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      if (throttleTimer) clearTimeout(throttleTimer);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [thresholds, eventName, nonInteraction, throttleMs]);
};

/**
 * Hook to track time on page
 */
export const useTimeOnPage = (
  options: {
    eventName?: string;
    milestones?: number[]; // Time milestones in seconds
    includeOnUnload?: boolean;
  } = {}
) => {
  const {
    eventName = "time_on_page",
    milestones = [10, 30, 60, 120, 300], // 10s, 30s, 1m, 2m, 5m
    includeOnUnload = true,
  } = options;

  useEffect(() => {
    // If no milestones provided or tracking disabled, skip setup
    if (milestones.length === 0 && !includeOnUnload) {
      return;
    }

    const startTime = new Date().getTime();
    const trackedMilestones = new Set<number>();
    let interval: ReturnType<typeof setInterval> | undefined;

    const trackTimeOnPage = (
      seconds: number,
      milestone: number | null = null
    ) => {
      trackEvent(eventName, {
        seconds_on_page: seconds,
        milestone: milestone,
        page_path: window.location.pathname,
        page_title: document.title,
        is_milestone: milestone !== null,
      });
    };

    // Check time milestones periodically - only if we have milestones to track
    if (milestones.length > 0) {
      interval = setInterval(() => {
        const timeNow = new Date().getTime();
        const timeOnPageSeconds = Math.floor((timeNow - startTime) / 1000);

        // Track milestones
        for (const milestone of milestones) {
          if (
            timeOnPageSeconds >= milestone &&
            !trackedMilestones.has(milestone)
          ) {
            trackedMilestones.add(milestone);
            trackTimeOnPage(timeOnPageSeconds, milestone);
          }
        }
      }, 1000); // Check every second
    }

    // Track final time when leaving
    const handleUnload = () => {
      const timeNow = new Date().getTime();
      const timeOnPageSeconds = Math.floor((timeNow - startTime) / 1000);

      // Use navigator.sendBeacon for more reliable tracking on page unload
      if (navigator.sendBeacon) {
        const data = new FormData();
        data.append("event", eventName);
        data.append("seconds_on_page", timeOnPageSeconds.toString());
        data.append("page_path", window.location.pathname);
        data.append("page_title", document.title);
        data.append("event_type", "page_exit");

        navigator.sendBeacon("/analytics", data);
      } else {
        trackTimeOnPage(timeOnPageSeconds);
      }
    };

    // Only add the beforeunload listener if tracking is enabled
    if (includeOnUnload) {
      window.addEventListener("beforeunload", handleUnload);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      window.removeEventListener("beforeunload", handleUnload);

      // Track final time when unmounting, but only if tracking is enabled
      if (milestones.length > 0 || includeOnUnload) {
        const timeNow = new Date().getTime();
        const timeOnPageSeconds = Math.floor((timeNow - startTime) / 1000);
        trackTimeOnPage(timeOnPageSeconds);
      }
    };
  }, [eventName, milestones, includeOnUnload]);
};
